import { useState, useEffect, useCallback } from 'react';
import { rentals } from '../services/api';

export const useServerSideRentals = () => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  
  // Sorting state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Data and loading state
  const [rentalsData, setRentalsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [search]);
  
  // Fetch rentals function
  const fetchRentals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {      const params = {
        page: currentPage,
        limit: 5,
        search: debouncedSearch,
        status: statusFilter !== 'all' ? statusFilter : '',
        partner: partnerFilter !== 'all' ? partnerFilter : '',
        startDate: dateFilter.startDate ? dateFilter.startDate.toISOString().split('T')[0] : '',
        endDate: dateFilter.endDate ? dateFilter.endDate.toISOString().split('T')[0] : '',
        paymentStatus: paymentStatusFilter !== 'all' ? paymentStatusFilter : '',
        sortBy,
        sortOrder
      };
      
      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      
      const response = await rentals.getAll(params);
      
      if (response.data && response.data.rentals) {
        // New pagination response format
        setRentalsData(response.data.rentals);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.totalCount);
        setHasNextPage(response.data.pagination.hasNextPage);
        setHasPrevPage(response.data.pagination.hasPrevPage);
      } else if (Array.isArray(response.data)) {
        // Fallback for old response format
        setRentalsData(response.data);
        setTotalPages(1);
        setTotalCount(response.data.length);
        setHasNextPage(false);
        setHasPrevPage(false);
      } else {
        setRentalsData([]);
        setTotalPages(1);
        setTotalCount(0);
        setHasNextPage(false);
        setHasPrevPage(false);
      }
    } catch (err) {
      console.error('Error fetching rentals:', err);
      setError(err.message || 'Failed to fetch rentals');
      setRentalsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    debouncedSearch,
    statusFilter,
    partnerFilter,
    dateFilter,
    paymentStatusFilter,
    sortBy,
    sortOrder
  ]);
  
  // Fetch rentals when dependencies change
  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);
  
  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearch, statusFilter, partnerFilter, dateFilter, paymentStatusFilter, sortBy, sortOrder]);
  
  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const goToPrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  // Filter handlers
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPartnerFilter('all');
    setDateFilter({
      startDate: null,
      endDate: null
    });
    setStartDate('');
    setEndDate('');
    setPaymentStatusFilter('all');
    setSortBy('createdAt');
    setSortOrder('desc');
  };
  
  return {
    // Data
    rentalsData,
    isLoading,
    error,
    
    // Pagination
    currentPage,
    totalPages,
    totalCount,
    hasNextPage,
    hasPrevPage,
    goToPage,
    goToNextPage,
    goToPrevPage,
    setCurrentPage,
    
    // Filters
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    partnerFilter,
    setPartnerFilter,
    dateFilter,
    setDateFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    paymentStatusFilter,
    setPaymentStatusFilter,
    clearFilters,
    
    // Sorting
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    
    // Actions
    refetch: fetchRentals
  };
};
